import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } from '../lib/api';
import { getTodayDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDays, Plus, ChevronLeft, ChevronRight, Loader2, Trash2, Clock, Edit } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: 'clase', label: 'Clase', color: '#2B4257', role: 'all' },
  { value: 'tarea', label: 'Tarea Operativa', color: '#D98A6C', role: 'all' },
  { value: 'evento', label: 'Evento Especial', color: '#4A7C59', role: 'all' },
  { value: 'mantencion', label: 'Mantención', color: '#C25953', role: 'all' },
  { value: 'estrategico', label: 'Estratégico', color: '#8B6F47', role: 'direccion' },
  { value: 'marketing', label: 'Marketing', color: '#6B8E7B', role: 'direccion' },
];

const HOURS = Array.from({ length: 14 }, (_, i) => {
  const h = i + 7;
  return `${h.toString().padStart(2, '0')}:00`;
});

const CalendarioOperativo = () => {
  const { isDirection } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '', event_type: 'clase', start_date: '', end_date: '',
    start_time: '09:00', end_time: '10:00', instructor: '', description: '', color: '#2B4257'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEvents(); }, [currentDate, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDateRange = () => {
    const d = new Date(currentDate);
    if (viewMode === 'day') {
      return { start: d, end: d };
    } else if (viewMode === 'week') {
      const dayOfWeek = d.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const start = new Date(d); start.setDate(d.getDate() + mondayOffset);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { start, end };
    } else {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { start, end };
    }
  };

  const fetchEvents = async () => {
    try {
      const { start, end } = getDateRange();
      const response = await getCalendarEvents({
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
      });
      setEvents(response.data);
    } catch (error) {
      
    } finally { setLoading(false); }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, formData);
        toast.success('Evento actualizado');
      } else {
        await createCalendarEvent({ ...formData, end_date: formData.end_date || formData.start_date });
        toast.success('Evento creado');
      }
      setShowEventDialog(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Error al guardar evento');
    } finally { setSaving(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteCalendarEvent(eventId);
      toast.success('Evento eliminado');
      setShowEventDialog(false);
      resetForm();
      fetchEvents();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({ title: '', event_type: 'clase', start_date: '', end_date: '', start_time: '09:00', end_time: '10:00', instructor: '', description: '', color: '#2B4257' });
  };

  const openNewEvent = (date) => {
    resetForm();
    setFormData(prev => ({ ...prev, start_date: date, end_date: date }));
    setShowEventDialog(true);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title, event_type: event.event_type, start_date: event.start_date,
      end_date: event.end_date || event.start_date, start_time: event.start_time || '09:00',
      end_time: event.end_time || '10:00', instructor: event.instructor || '',
      description: event.description || '', color: event.color || '#2B4257'
    });
    setShowEventDialog(true);
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + dir);
    else if (viewMode === 'week') d.setDate(d.getDate() + (dir * 7));
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const openDayView = (dateStr) => {
    setCurrentDate(new Date(dateStr + 'T12:00:00'));
    setViewMode('day');
  };

  const weekDays = useMemo(() => {
    const { start } = getDateRange();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthDays = useMemo(() => {
    if (viewMode !== 'month') return [];
    const d = new Date(currentDate);
    const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = -startPad; i <= lastDay.getDate() - 1; i++) {
      const date = new Date(d.getFullYear(), d.getMonth(), i + 1);
      days.push(date);
    }
    return days;
  }, [currentDate, viewMode]);

  const getEventsForDate = (dateStr) => {
    return events.filter(e => {
      if (e.start_date !== dateStr) return false;
      // Role-based filtering: hide strategic/marketing events from reception
      if (!isDirection()) {
        const eventType = EVENT_TYPES.find(t => t.value === e.event_type);
        if (eventType && eventType.role === 'direccion') return false;
      }
      return true;
    });
  };

  const visibleEventTypes = isDirection() ? EVENT_TYPES : EVENT_TYPES.filter(t => t.role === 'all');

  const getTypeColor = (type) => EVENT_TYPES.find(t => t.value === type)?.color || '#2B4257';
  const getTypeLabel = (type) => EVENT_TYPES.find(t => t.value === type)?.label || type;

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const todayStr = getTodayDate();

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="calendar-loading"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendario-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Calendario Operativo</h1>
          <p className="page-subtitle">Gestión de clases, tareas y eventos del estudio</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => openNewEvent(todayStr)} data-testid="btn-new-event">
          <Plus size={18} /> Nuevo Evento
        </Button>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="btn-prev"><ChevronLeft size={20} /></Button>
            <h2 className="text-lg font-semibold text-[#1A2835] min-w-[200px] text-center">
              {viewMode === 'day'
                ? currentDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                : viewMode === 'week'
                ? `${weekDays[0]?.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })} - ${weekDays[6]?.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`
                : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate(1)} data-testid="btn-next"><ChevronRight size={20} /></Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={viewMode === 'day' ? 'default' : 'outline'} size="sm" onClick={() => { setViewMode('day'); setCurrentDate(new Date()); }}
              className={viewMode === 'day' ? 'btn-primary' : 'btn-secondary'} data-testid="btn-view-day">Día</Button>
            <Button variant={viewMode === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('week')}
              className={viewMode === 'week' ? 'btn-primary' : 'btn-secondary'} data-testid="btn-view-week">Semana</Button>
            <Button variant={viewMode === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('month')}
              className={viewMode === 'month' ? 'btn-primary' : 'btn-secondary'} data-testid="btn-view-month">Mes</Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E8E2D7]">
          {visibleEventTypes.map(t => (
            <div key={t.value} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
              <span className="text-[#5C7083]">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="card p-0 overflow-hidden" data-testid="week-view">
          <div className="grid grid-cols-7 border-b border-[#E8E2D7]">
            {weekDays.map((day, i) => {
              const dateStr = day.toISOString().split('T')[0];
              const isToday = dateStr === todayStr;
              return (
                <div key={dateStr} className={`p-3 text-center border-r border-[#E8E2D7] last:border-r-0 cursor-pointer hover:opacity-80 ${isToday ? 'bg-[#2B4257] text-white' : 'bg-[#F5F0E8]'}`}
                  onClick={() => openDayView(dateStr)}>
                  <p className="text-xs font-medium">{dayNames[i]}</p>
                  <p className="text-lg font-bold">{day.getDate()}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 min-h-[400px]">
            {weekDays.map((day, i) => {
              const dateStr = day.toISOString().split('T')[0];
              const dayEvents = getEventsForDate(dateStr);
              return (
                <div key={`events-${dateStr}`} className="border-r border-[#E8E2D7] last:border-r-0 p-2 space-y-1 cursor-pointer hover:bg-[#F5F0E8]/50 transition-colors"
                  onClick={() => openNewEvent(dateStr)} data-testid={`week-day-${dateStr}`}>
                  {dayEvents.map(ev => (
                    <div key={ev.id} className="p-2 rounded-lg text-white text-xs cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: ev.color || getTypeColor(ev.event_type) }}
                      onClick={(e) => { e.stopPropagation(); openEditEvent(ev); }} data-testid={`event-${ev.id}`}>
                      <p className="font-semibold truncate">{ev.title}</p>
                      <div className="flex items-center gap-1 mt-0.5 opacity-80"><Clock size={10} /><span>{ev.start_time} - {ev.end_time}</span></div>
                      {ev.instructor && <p className="opacity-80 truncate mt-0.5">{ev.instructor}</p>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (() => {
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayEvents = getEventsForDate(dateStr);
        return (
          <div className="card" data-testid="day-view">
            <h2 className="text-lg font-semibold text-[#1A2835] mb-4">
              {currentDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })}
              <span className="text-sm font-normal text-[#5C7083] ml-2">({dayEvents.length} eventos)</span>
            </h2>
            {dayEvents.length === 0 ? (
              <div className="text-center py-12 text-[#5C7083]">
                <CalendarDays className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
                <p>No hay eventos para este día</p>
                <Button className="btn-primary mt-4 gap-2" onClick={() => openNewEvent(dateStr)}>
                  <Plus size={16} /> Agregar Evento
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {HOURS.map(hour => {
                  const hourEvents = dayEvents.filter(e => e.start_time && e.start_time.startsWith(hour.slice(0, 2)));
                  return (
                    <div key={hour} className="flex gap-4 min-h-[50px]" data-testid={`day-hour-${hour}`}>
                      <div className="w-16 text-sm text-[#5C7083] pt-1 flex-shrink-0 font-medium">{hour}</div>
                      <div className="flex-1 border-t border-[#E8E2D7] pt-1 space-y-1">
                        {hourEvents.map(ev => (
                          <div key={ev.id} className="p-3 rounded-lg text-white cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: ev.color || getTypeColor(ev.event_type) }}
                            onClick={() => openEditEvent(ev)} data-testid={`day-event-${ev.id}`}>
                            <div className="flex items-center justify-between">
                              <p className="font-semibold">{ev.title}</p>
                              <span className="text-xs opacity-80">{ev.start_time} - {ev.end_time}</span>
                            </div>
                            {ev.instructor && <p className="text-sm opacity-80 mt-1">Instructor: {ev.instructor}</p>}
                            {ev.description && <p className="text-sm opacity-70 mt-1">{ev.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Month View */}
      {viewMode === 'month' && (
        <div className="card p-0 overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 border-b border-[#E8E2D7]">
            {dayNames.map(d => <div key={d} className="p-2 text-center text-xs font-semibold text-[#5C7083] bg-[#F5F0E8]">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((day, i) => {
              const dateStr = day.toISOString().split('T')[0];
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = dateStr === todayStr;
              const dayEvents = getEventsForDate(dateStr);
              return (
                <div key={`month-${dateStr}`} className={`min-h-[90px] p-1.5 border-r border-b border-[#E8E2D7] cursor-pointer hover:bg-[#F5F0E8]/30 transition-colors ${!isCurrentMonth ? 'opacity-30' : ''}`}
                  onClick={() => openNewEvent(dateStr)}>
                  <p className={`text-xs font-medium mb-1 ${isToday ? 'bg-[#2B4257] text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-[#5C7083]'}`}>{day.getDate()}</p>
                  {dayEvents.slice(0, 3).map(ev => (
                    <div key={ev.id} className="text-[10px] px-1.5 py-0.5 rounded text-white mb-0.5 truncate cursor-pointer"
                      style={{ backgroundColor: ev.color || getTypeColor(ev.event_type) }}
                      onClick={(e) => { e.stopPropagation(); openEditEvent(ev); }}>
                      {ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <p className="text-[10px] text-[#5C7083]">+{dayEvents.length - 3} más</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={(v) => { if (!v) resetForm(); setShowEventDialog(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle><DialogDescription>Completa los datos del evento para el calendario operativo</DialogDescription></DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-4" data-testid="event-form">
            <div>
              <Label className="form-label">Título *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="form-input" required data-testid="input-event-title" />
            </div>
            <div>
              <Label className="form-label">Tipo *</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v, color: getTypeColor(v) })}>
                <SelectTrigger data-testid="select-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{visibleEventTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Fecha *</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value, end_date: e.target.value })}
                  className="form-input" required data-testid="input-event-date" />
              </div>
              <div>
                <Label className="form-label">Instructor</Label>
                <Input value={formData.instructor} onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  className="form-input" placeholder="Nombre" data-testid="input-event-instructor" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Hora Inicio</Label>
                <Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="form-input" data-testid="input-event-start-time" />
              </div>
              <div>
                <Label className="form-label">Hora Fin</Label>
                <Input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="form-input" data-testid="input-event-end-time" />
              </div>
            </div>
            <div>
              <Label className="form-label">Descripción</Label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input min-h-[60px] resize-none" data-testid="input-event-description" />
            </div>
            <div className="flex justify-between">
              <div>{editingEvent && (
                <Button type="button" variant="ghost" className="text-[#C25953] hover:text-[#C25953] hover:bg-[#FBEAE9]" onClick={() => handleDeleteEvent(editingEvent.id)} data-testid="btn-delete-event">
                  <Trash2 size={16} className="mr-1" /> Eliminar
                </Button>
              )}</div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => { resetForm(); setShowEventDialog(false); }} className="btn-secondary">Cancelar</Button>
                <Button type="submit" className="btn-primary" disabled={saving || !formData.title || !formData.start_date} data-testid="btn-save-event">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingEvent ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarioOperativo;
